<template>
    <ImageCarousel
        ref="carouselRef"
        :files="slides"
        :initial-index="startIndex"
        :show-counter="false"
        @update:index="onIndex"
    >
        <template #slide="{ file: slide, isCurrent, inWindow }">
            <template v-if="kindOf(slide) === 'image'">
                <img
                    v-if="inWindow && urlOf(slide)"
                    :src="urlOf(slide)!"
                    :alt="slide.name"
                    class="pointer-events-none block h-full w-full object-cover select-none md:object-contain"
                    draggable="false"
                    @error="reportError(slide)"
                />
                <MuvalLoader
                    v-else-if="inWindow"
                    size="l"
                />
            </template>
            <!-- Video, PDF and text mount on the current slide only: never twice, never playing off-screen. -->
            <template v-else-if="kindOf(slide) && isCurrent">
                <MuvalLoader
                    v-if="!urlOf(slide)"
                    size="l"
                />
                <video
                    v-else-if="kindOf(slide) === 'video'"
                    :src="urlOf(slide)!"
                    controls
                    playsinline
                    preload="metadata"
                    class="block h-full w-full object-contain"
                    data-test="file-viewer-video"
                    @error="reportError(slide)"
                    @keydown.left.stop
                    @keydown.right.stop
                ></video>
                <iframe
                    v-else-if="kindOf(slide) === 'pdf'"
                    :src="urlOf(slide)!"
                    :title="slide.name"
                    class="h-full w-full border-0 bg-white"
                    data-test="file-viewer-pdf"
                ></iframe>
                <iframe
                    v-else
                    :src="urlOf(slide)!"
                    :title="slide.name"
                    sandbox=""
                    class="h-full w-full border-0 bg-white"
                    data-test="file-viewer-text"
                ></iframe>
            </template>
            <div
                v-else
                class="flex max-w-md flex-col items-center gap-3 px-6 text-center text-white"
                data-test="file-viewer-file-card"
            >
                <div class="relative">
                    <SvgIcon
                        :name="failureOf(slide) ? 'system-warning' : iconFor(slide)"
                        class="size-16 text-white/80"
                    />
                    <span
                        v-if="extensionOf(slide) && !failureOf(slide)"
                        class="absolute bottom-4 left-0.5 rounded-xs px-1.5 text-xs leading-tight font-medium text-white uppercase"
                        :class="getExtensionBgClass(extensionOf(slide))"
                    >
                        {{ extensionOf(slide) }}
                    </span>
                </div>
                <span class="text-base font-medium break-all">{{ slide.name }}</span>
                <span
                    v-if="failureOf(slide)"
                    class="text-sm text-white/70"
                    data-test="file-viewer-preview-failure"
                >
                    {{ failureSentence(slide) }}
                </span>
                <span
                    v-else-if="!kindOf(slide)"
                    class="text-sm text-white/70"
                >
                    No preview for this file type
                </span>
            </div>
        </template>

        <template #unavailable="{ file: slide }">
            <div
                class="flex flex-col items-center gap-3 text-center text-sm text-white"
                data-test="file-viewer-unavailable"
            >
                <span>Preview unavailable</span>
                <MuvalButton
                    quinary
                    small
                    type="button"
                    @click="openFile(slide.id)"
                >
                    Open in new tab
                </MuvalButton>
            </div>
        </template>
    </ImageCarousel>
</template>

<script setup lang="ts">
import MuvalButton from '@buttons/MuvalButton.vue';
import ImageCarousel from '@components/inventory/conditionReport/modals/ImageCarousel.vue';
import MuvalLoader from '@components/loader/MuvalLoader.vue';
import SvgIcon from '@components/utilities/SvgIcon.vue';
import {
    getExtensionBgClass,
    getFileDisplayName,
    getFileExtension,
    isImageFile,
    previewFailure,
    previewKind,
} from '@utilities/fileDisplay';

/**
 * Pages through a file list in the ICR carousel. Images, video, PDF and text show in
 * place; any other file shows its icon and name, and why when the API couldn't preview it.
 * The caller supplies links and owns the header.
 */

type FileRecord = Record<string, any>;
type Slide = { id?: string | number; name?: string };

const props = withDefaults(
    defineProps<{
        files: FileRecord[];
        startIndex?: number;
        /** The link to show a file from, or null while it is still being fetched. */
        urlFor: (file: FileRecord) => string | null;
        /** Ids whose preview has failed; they show a fallback panel instead. */
        unavailableIds?: Set<string | number>;
        getLabel?: (file: FileRecord) => string;
        /** Whether the surface offers Download for a file, so the viewer only says it can be downloaded where it can. */
        canDownload?: (file: FileRecord) => boolean;
    }>(),
    { startIndex: 0, unavailableIds: () => new Set(), getLabel: getFileDisplayName, canDownload: () => true },
);

const emit = defineEmits<{
    'update:index': [index: number];
    /** A file came into view. The caller fetches its link, or a fresh one once the link it holds has aged. */
    'need-url': [file: FileRecord];
    'preview-error': [file: FileRecord];
    /** The page's Content-Security-Policy refused the preview, so retrying cannot help. */
    'preview-blocked': [file: FileRecord];
    open: [file: FileRecord];
}>();

const carouselRef = ref<InstanceType<typeof ImageCarousel> | null>(null);
const currentIndex = ref(props.startIndex);

const slides = computed(() =>
    props.files.map((file) => ({
        id: file.id,
        url: '',
        name: props.getLabel(file),
        mime_type: file.mime_type,
        unavailable: props.unavailableIds.has(file.id),
    })),
);

const fileFor = (slide: Slide) => props.files.find((file) => file.id === slide.id);
const kindOf = (slide: Slide) => {
    const file = fileFor(slide);

    return file ? previewKind(file) : null;
};
const extensionOf = (slide: Slide) => {
    const file = fileFor(slide);

    return file ? getFileExtension(file) : null;
};
const failureOf = (slide: Slide) => {
    const file = fileFor(slide);

    return file ? previewFailure(file) : null;
};
const failureSentence = (slide: Slide) => {
    const file = fileFor(slide);
    const failure = file ? previewFailure(file) : null;

    if (!file || !failure) return '';

    return props.canDownload(file) ? `${failure.message} You can still download it.` : failure.message;
};
const iconFor = (slide: Slide) => {
    const file = fileFor(slide);

    if (file && isImageFile(file)) return 'system-photo';

    return file && previewKind(file) === 'video' ? 'system-play' : 'system-document';
};
const urlOf = (slide: Slide) => {
    const file = fileFor(slide);

    return file ? props.urlFor(file) : null;
};

const reportError = (slide: Slide) => {
    const file = fileFor(slide);

    if (file) emit('preview-error', file);
};

const openFile = (id?: string | number) => {
    const file = props.files.find((item) => item.id === id);

    if (file) emit('open', file);
};

const onIndex = (index: number) => {
    currentIndex.value = index;
    emit('update:index', index);
};

/** The current file, plus the images either side so paging to them is instant. */
const filesNeedingUrls = computed(() => {
    const count = props.files.length;

    if (!count) return [];

    const current = props.files[currentIndex.value];
    const neighbours =
        count > 1 ?
            [props.files[(currentIndex.value + 1) % count], props.files[(currentIndex.value - 1 + count) % count]]
        :   [];

    return [current, ...neighbours.filter((file) => previewKind(file) === 'image')].filter(
        (file) => file && previewKind(file) !== null,
    );
});

/*
 * Asks only for files coming into view, after the carousel has followed its file through a list change.
 * So a refetch never fetches for a file nobody opened, nor reloads the slide on screen.
 */
let inView = new Set<string | number>();

watch(
    filesNeedingUrls,
    (files) => {
        files.forEach((file) => {
            if (!inView.has(file.id)) emit('need-url', file);
        });
        inView = new Set(files.map((file) => file.id));
    },
    { immediate: true, flush: 'post' },
);

/** A refused frame or video fires no error of its own; the page hears about it here instead. */
const onPolicyViolation = (event: SecurityPolicyViolationEvent) => {
    if (event.disposition !== 'enforce' || !/^(frame|media|child)-src/.test(event.effectiveDirective)) return;

    const file = props.files[currentIndex.value];
    const url = file ? props.urlFor(file) : null;

    if (!url || !event.blockedURI) return;

    try {
        if (new URL(event.blockedURI).origin !== new URL(url).origin) return;
    } catch {
        return;
    }

    emit('preview-blocked', file);
};

// Focus lands on the carousel so ← and → page straight away.
onMounted(() => {
    document.addEventListener('securitypolicyviolation', onPolicyViolation);
    nextTick(() => (carouselRef.value?.$el as HTMLElement | undefined)?.focus({ preventScroll: true }));
});

onBeforeUnmount(() => {
    document.removeEventListener('securitypolicyviolation', onPolicyViolation);
});
</script>
